import React, { useState, useEffect } from 'react';
import { auth, db } from '../services/firebase/config';
import { collection, query, where, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import '../styles/adminPanel.css';
import { Admin, Resource } from 'react-admin';
import { useAuth } from '../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';

const AdminPanel = () => {
    const { currentUser } = useAuth();
    const [isAdmin, setIsAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const functions = getFunctions();
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalArticles: 0,
        totalComments: 0,
        activeUsers: 0
    });
    const [users, setUsers] = useState([]);
    const [reports, setReports] = useState([]);
    const [notification, setNotification] = useState({ message: '', type: '' });

    useEffect(() => {
        const checkAdminStatus = async () => {
            if (!currentUser) {
                setLoading(false);
                return;
            }

            try {
                const checkAdmin = httpsCallable(functions, 'checkAdminStatus');
                const result = await checkAdmin();
                setIsAdmin(result.data.isAdmin);
            } catch (error) {
                console.error('Error checking admin status:', error);
                setIsAdmin(false);
            } finally {
                setLoading(false);
            }
        };

        checkAdminStatus();
    }, [currentUser, functions]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(user => {
            if (user && user.email === 'ana9non@gmail.com') {
                fetchStats();
                fetchUsers();
                fetchReports();
            }
        });

        return () => unsubscribe();
    }, []);

    const fetchStats = async () => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const articlesSnapshot = await getDocs(collection(db, 'articles'));
            const commentsSnapshot = await getDocs(collection(db, 'comments'));

            setStats({
                totalUsers: usersSnapshot.size,
                totalArticles: articlesSnapshot.size,
                totalComments: commentsSnapshot.size,
                activeUsers: usersSnapshot.docs.filter(doc => doc.data().lastActive > Date.now() - 7 * 24 * 60 * 60 * 1000).length
            });
        } catch (error) {
            showNotification('Error fetching statistics', 'error');
        }
    };

    const fetchUsers = async () => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            setUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            showNotification('Error fetching user data', 'error');
        }
    };

    const fetchReports = async () => {
        try {
            const reportsSnapshot = await getDocs(collection(db, 'reports'));
            const reports = reportsSnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    createdAt: data.createdAt?.toDate()?.toLocaleString() || 'Unknown date'
                };
            });
            setReports(reports);
        } catch (error) {
            showNotification('Error fetching reports', 'error');
        }
    };

    const handleUserAction = async (userId, action) => {
        try {
            const userRef = doc(db, 'users', userId);
            
            switch(action) {
                case 'ban':
                    await updateDoc(userRef, {
                        isBanned: true,
                        banExpiry: Date.now() + 7 * 24 * 60 * 60 * 1000 // 7 days
                    });
                    showNotification('User banned for 7 days', 'success');
                    break;
                case 'warn':
                    await updateDoc(userRef, {
                        warnings: (users.find(u => u.id === userId)?.warnings || 0) + 1
                    });
                    showNotification('Warning sent to user', 'success');
                    break;
                case 'delete':
                    await deleteDoc(userRef);
                    showNotification('User account deleted', 'success');
                    break;
            }
            
            fetchUsers();
        } catch (error) {
            showNotification('Error executing action', 'error');
        }
    };

    const handleReportAction = async (reportId, action) => {
        try {
            const reportRef = doc(db, 'reports', reportId);
            
            if (action === 'resolve') {
                await updateDoc(reportRef, { 
                    status: 'resolved',
                    resolvedAt: new Date(),
                    resolvedBy: currentUser.email
                });
                showNotification('Report resolved successfully', 'success');
            } else if (action === 'dismiss') {
                await updateDoc(reportRef, { 
                    status: 'dismissed',
                    dismissedAt: new Date(),
                    dismissedBy: currentUser.email
                });
                showNotification('Report dismissed', 'success');
            }
            
            fetchReports();
        } catch (error) {
            showNotification('Error processing report', 'error');
        }
    };

    const showNotification = (message, type) => {
        setNotification({ message, type });
        setTimeout(() => setNotification({ message: '', type: '' }), 3000);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!currentUser || !isAdmin) {
        return <Navigate to="/" replace />;
    }

    const getReportTypeLabel = (type) => {
        const types = {
            'hate-speech': 'Hate Speech',
            'harassment': 'Harassment & Bullying',
            'violence': 'Violence',
            'child-safety': 'Child Safety',
            'privacy': 'Privacy & Security',
            'spam': 'Spam',
            'self-harm': 'Self-harm',
            'violent-groups': 'Violent Organizations'
        };
        return types[type] || type;
    };

    return (
        <Admin
            dataProvider={dataProvider}
            authProvider={authProvider}
        >
            <div className="admin-panel">
                <div className="admin-header">
                    <h1 className="admin-title">Admin Dashboard</h1>
                </div>

                {notification.message && (
                    <div className={`notification notification-${notification.type}`}>
                        {notification.message}
                    </div>
                )}

                <div className="admin-stats">
                    <div className="stat-card">
                        <div className="stat-number">{stats.totalUsers}</div>
                        <div className="stat-label">Total Users</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{stats.totalArticles}</div>
                        <div className="stat-label">Total Articles</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{stats.totalComments}</div>
                        <div className="stat-label">Total Comments</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{stats.activeUsers}</div>
                        <div className="stat-label">Active Users</div>
                    </div>
                </div>

                <div className="admin-sections">
                    <div className="admin-section">
                        <h2 className="section-title">User Management</h2>
                        <div className="user-list">
                            {users.map(user => (
                                <div key={user.id} className="list-item">
                                    <div>
                                        <strong>{user.displayName || user.name}</strong>
                                        <div>{user.email}</div>
                                        {user.warnings > 0 && (
                                            <div className="warning-count">
                                                Warnings: {user.warnings}
                                            </div>
                                        )}
                                        {user.isBanned && (
                                            <div className="ban-status">
                                                Banned until: {new Date(user.banExpiry).toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                    <div className="action-buttons">
                                        <button 
                                            className="admin-button admin-button-primary"
                                            onClick={() => handleUserAction(user.id, 'warn')}
                                        >
                                            Warn
                                        </button>
                                        <button 
                                            className="admin-button admin-button-danger"
                                            onClick={() => handleUserAction(user.id, 'ban')}
                                        >
                                            Ban
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="admin-section">
                        <h2 className="section-title">Reports</h2>
                        <div className="reports-list">
                            {reports.map(report => (
                                <div key={report.id} className="list-item">
                                    <div className="report-content">
                                        <div className="report-header">
                                            <strong>{getReportTypeLabel(report.type)}</strong>
                                            <span className="report-date">{report.createdAt}</span>
                                        </div>
                                        <div className="report-details">
                                            <p>{report.description}</p>
                                            <div className="report-meta">
                                                <span>Reported by: {report.reportedBy}</span>
                                                <span>Content ID: {report.contentId}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="action-buttons">
                                        <button 
                                            className="admin-button admin-button-primary"
                                            onClick={() => handleReportAction(report.id, 'resolve')}
                                        >
                                            Resolve
                                        </button>
                                        <button 
                                            className="admin-button admin-button-danger"
                                            onClick={() => handleReportAction(report.id, 'dismiss')}
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Admin>
    );
};

export default AdminPanel; 